/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Directive,
  ElementRef,
  Renderer2,
  effect,
  input,
  inject,
} from '@angular/core';

export type LoadingPosition = 'left' | 'right';

@Directive()
export abstract class LoadingDirective {
  protected _elementRef = inject(ElementRef);
  protected _renderer = inject(Renderer2);
  protected _spinnerElement: SVGElement | null = null;
  protected _dotsElement: HTMLElement | null = null;
  protected _shouldAddDots = true;

  loading = input(false);
  loadingPosition = input<LoadingPosition>('right');

  constructor() {
    effect(() => {
      const loading = this.loading();
      const position = this.loadingPosition();
      if (loading) {
        this._addSpinner(position);
      } else {
        this._removeSpinner();
      }
    });
  }

  private _addSpinner(position: LoadingPosition) {
    if (this._spinnerElement) {
      this._moveElements(position);
      return;
    }

    this._hideChildren();
    this._createSpinner();
    this._insertElements(position);
  }

  private _moveElements(position: LoadingPosition) {
    const needsMove =
      position === 'left'
        ? this._elementRef.nativeElement.firstChild !== this._spinnerElement
        : this._elementRef.nativeElement.lastChild !== this._spinnerElement;

    if (!needsMove) return;

    if (position === 'left') {
      this._elementRef.nativeElement.insertBefore(
        this._spinnerElement,
        this._elementRef.nativeElement.firstChild,
      );
      if (this._dotsElement) {
        this._elementRef.nativeElement.insertBefore(
          this._dotsElement!,
          this._spinnerElement!.nextSibling,
        );
      }
    } else {
      if (this._dotsElement) {
        this._elementRef.nativeElement.appendChild(this._dotsElement);
      }
      this._elementRef.nativeElement.appendChild(this._spinnerElement);
    }
  }

  private _createSpinner() {
    const svg = this._createSvgElement();
    this._addSpinnerCircles(svg);
    this._spinnerElement = svg;

    if (this._shouldAddDots) {
      this._createDotsElement();
    }
  }

  private _createSvgElement() {
    const svg = this._renderer.createElement('svg', 'svg');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.style.flexShrink = '0';
    svg.style.display = 'inline-block';
    return svg;
  }

  private _addSpinnerCircles(svg: SVGElement) {
    const circle1 = this._renderer.createElement('circle', 'svg');
    circle1.setAttribute('stroke', 'currentColor');
    circle1.setAttribute('opacity', '0.3');
    circle1.setAttribute('cx', '10');
    circle1.setAttribute('cy', '10');
    circle1.setAttribute('r', '9');
    circle1.setAttribute('fill', 'none');
    circle1.setAttribute('stroke-width', '2');
    svg.appendChild(circle1);

    const circle2 = this._renderer.createElement('circle', 'svg');
    circle2.setAttribute('stroke', 'currentColor');
    circle2.setAttribute('cx', '10');
    circle2.setAttribute('cy', '10');
    circle2.setAttribute('r', '9');
    circle2.setAttribute('fill', 'none');
    circle2.setAttribute('stroke-width', '2');
    circle2.setAttribute('stroke-dasharray', '12.5 50');
    circle2.setAttribute('stroke-linecap', 'round');

    this._addSpinnerAnimation(circle2);
    svg.appendChild(circle2);
  }

  private _addSpinnerAnimation(circle: SVGCircleElement) {
    const animate = this._renderer.createElement('animateTransform', 'svg');
    animate.setAttribute('attributeName', 'transform');
    animate.setAttribute('type', 'rotate');
    animate.setAttribute('from', '0 10 10');
    animate.setAttribute('to', '360 10 10');
    animate.setAttribute('dur', '1s');
    animate.setAttribute('repeatCount', 'indefinite');
    circle.appendChild(animate);
  }

  private _createDotsElement() {
    const dots = this._renderer.createElement('span');
    const textNode = this._renderer.createText('...');
    this._renderer.appendChild(dots, textNode);
    this._dotsElement = dots;
  }

  private _insertElements(position: LoadingPosition) {
    const svg = this._spinnerElement;
    if (position === 'left') {
      this._elementRef.nativeElement.insertBefore(
        svg,
        this._elementRef.nativeElement.firstChild,
      );
      if (this._shouldAddDots) {
        this._elementRef.nativeElement.insertBefore(
          this._dotsElement!,
          svg!.nextSibling,
        );
      }
    } else {
      if (this._shouldAddDots) {
        this._elementRef.nativeElement.appendChild(this._dotsElement);
      }
      this._elementRef.nativeElement.appendChild(svg);
    }
  }

  protected _removeSpinner() {
    if (this._spinnerElement) {
      this._renderer.removeChild(
        this._elementRef.nativeElement,
        this._spinnerElement,
      );
      this._spinnerElement = null;
    }

    if (this._dotsElement) {
      this._renderer.removeChild(
        this._elementRef.nativeElement,
        this._dotsElement,
      );
      this._dotsElement = null;
    }

    this._showChildren();
  }

  protected _hideChildren() {}

  protected _showChildren() {}
}
